import { useState, useEffect } from 'react';
import { Button, Input, FormGroup, Label, Alert, Progress } from 'reactstrap';
import { toast } from 'react-toastify';
import TournamentDetailCard from './TournamentDetailCard';
import { bulkRegisterPlayersRest, getBulkRegistrationStatus } from '@/services/tournaments/tournaments-service';
import { Environment, environment } from '@repo/environment';

interface BulkRegistrationSectionProps {
  tournament: {
    id: string;
    contractAddress?: string | null;
  };
  onRefetch: () => void;
}

interface BulkRegistrationResult {
  success: boolean;
  message: string;
  queued?: number;
  failed?: number;
  errors?: string[];
  transactions?: Array<{
    userId: string;
    transactionId?: string;
    status: 'queued' | 'failed';
    error?: string;
  }>;
}

interface RegistrationStatus {
  confirmed: number;
  pending: number;
  failed: number;
}

const BulkRegistrationSection = ({ tournament, onRefetch }: BulkRegistrationSectionProps) => {
  const [bulkRegisterCount, setBulkRegisterCount] = useState<number>(1);
  const [isBulkRegistering, setIsBulkRegistering] = useState(false);
  const [lastResult, setLastResult] = useState<BulkRegistrationResult | null>(null);
  const [registrationProgress, setRegistrationProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState<RegistrationStatus | null>(null);

  // Only show in non-production environments
  if (environment === Environment.PRODUCTION) {
    return null;
  }

  // Monitor registration status using API when transaction IDs are available
  useEffect(() => {
    if (isBulkRegistering && lastResult?.transactions) {
      const transactionIds = lastResult.transactions.filter((t) => t.transactionId).map((t) => t.transactionId!);

      if (transactionIds.length === 0) return;

      const checkStatus = async () => {
        try {
          const statusResponse = await getBulkRegistrationStatus(transactionIds);
          if (statusResponse.success) {
            setCurrentStatus(statusResponse.status);

            // Calculate progress based on confirmed + failed vs total
            const completed = statusResponse.status.confirmed + statusResponse.status.failed;
            const total = transactionIds.length;
            const progress = (completed / total) * 100;
            setRegistrationProgress(progress);

            // If all transactions are completed, stop monitoring
            if (completed >= total) {
              setIsBulkRegistering(false);
              onRefetch();
              toast.success(
                `Registration complete! ${statusResponse.status.confirmed} confirmed, ${statusResponse.status.failed} failed`,
              );
            }
          }
        } catch (error) {
          console.error('Failed to check registration status:', error);
        }
      };

      // Check status immediately and then every 5 seconds
      checkStatus();
      const interval = setInterval(checkStatus, 5000);

      return () => clearInterval(interval);
    }
  }, [isBulkRegistering, lastResult?.transactions, onRefetch]);

  // Fallback progress simulation when transaction IDs are not available
  useEffect(() => {
    if (isBulkRegistering && lastResult?.queued && !lastResult?.transactions?.some((t) => t.transactionId)) {
      const interval = setInterval(() => {
        setRegistrationProgress((prev) => {
          if (prev >= 95) {
            // Cap at 95% for simulation
            return prev;
          }
          return prev + 95 / (lastResult.queued || 1) / 15; // Estimate based on 15-second delays
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isBulkRegistering, lastResult?.queued, lastResult?.transactions]);

  const handleBulkRegister = async () => {
    if (!tournament || !bulkRegisterCount || bulkRegisterCount <= 0) {
      toast.error('Please enter a valid number of players');
      return;
    }

    setIsBulkRegistering(true);
    setLastResult(null);
    setRegistrationProgress(0);
    setCurrentStatus(null);

    try {
      const result = await bulkRegisterPlayersRest(tournament.id, bulkRegisterCount);
      setLastResult(result);

      if (result.success) {
        toast.success(`Successfully queued ${result.queued} players for registration`);
        setBulkRegisterCount(1);
        setRegistrationProgress(5); // Initial progress

        // If no transaction IDs available, use fallback timer
        if (!result.transactions?.some((t) => t.transactionId)) {
          const estimatedTime = (result.queued || 1) * 15000; // 15 seconds per registration
          setTimeout(() => {
            onRefetch();
            setIsBulkRegistering(false);
            setRegistrationProgress(100);
          }, estimatedTime);
        }
      } else {
        toast.error(result.message || 'Failed to register players');
        setIsBulkRegistering(false);
      }
    } catch (error: any) {
      console.error('Bulk registration error:', error);
      toast.error(error?.response?.data?.message || 'Failed to register players');
      setIsBulkRegistering(false);
    }
  };

  return (
    <div className="w-100 my-3">
      <TournamentDetailCard title="Bulk Registration (Testing Only)" className="w-100">
        <Alert color="warning" className="mb-3">
          <strong>⚠️ For Testing Only</strong> - This feature is for development and testing purposes only.
        </Alert>

        <div className="d-flex align-items-end justify-content-start mb-3 gap-3">
          <div className="d-flex flex-column" style={{ minWidth: '200px' }}>
            <Label for="bulkRegisterCount" className="form-label mb-1">
              Number of Players to Register
            </Label>
            <Input
              id="bulkRegisterCount"
              type="number"
              min="1"
              max="10000"
              value={bulkRegisterCount}
              onChange={(e) => setBulkRegisterCount(parseInt(e.target.value) || 1)}
              disabled={isBulkRegistering}
              className="form-control"
              style={{ height: '38px' }}
            />
          </div>

          <Button
            color="primary"
            onClick={handleBulkRegister}
            disabled={isBulkRegistering || !tournament?.contractAddress}
            className="d-flex align-items-center justify-content-center gap-2"
            style={{ height: '38px', minWidth: '120px' }}
          >
            {isBulkRegistering ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Registering...
              </>
            ) : (
              'Register'
            )}
          </Button>
        </div>

        {/* Progress Bar */}
        {isBulkRegistering && lastResult?.queued && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-muted">Registration Progress</small>
              <small className="text-muted">{Math.round(registrationProgress)}%</small>
            </div>
            <Progress value={registrationProgress} color="primary" />
            <div className="d-flex justify-content-between align-items-center mt-1">
              <small className="text-muted">
                {currentStatus
                  ? `Confirmed: ${currentStatus.confirmed}, Pending: ${currentStatus.pending}, Failed: ${currentStatus.failed}`
                  : `Processing ${lastResult.queued} registrations...`}
              </small>
              {currentStatus && (
                <small className="text-muted">
                  {currentStatus.confirmed + currentStatus.failed}/{lastResult.queued} complete
                </small>
              )}
            </div>
          </div>
        )}

        {/* Results Display */}
        {lastResult && !isBulkRegistering && (
          <div className="mt-3">
            <Alert color={lastResult.success ? 'success' : 'danger'} className="mb-2">
              <strong>Registration Complete:</strong> {lastResult.message}
            </Alert>
            {currentStatus ? (
              <div className="row">
                <div className="col-md-4">
                  <div className="text-success">
                    <small>✅ Confirmed: {currentStatus.confirmed}</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-warning">
                    <small>⏳ Pending: {currentStatus.pending}</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-danger">
                    <small>❌ Failed: {currentStatus.failed}</small>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {!!(lastResult.queued && lastResult.queued > 0) && (
                  <div className="text-success">
                    <small>✅ Successfully queued: {lastResult.queued} players</small>
                  </div>
                )}
                {!!(lastResult.failed && lastResult.failed > 0) && (
                  <div className="text-danger">
                    <small>❌ Failed: {lastResult.failed} players</small>
                  </div>
                )}
              </>
            )}
            {lastResult.errors && lastResult.errors.length > 0 && (
              <details className="mt-2">
                <summary className="text-muted" style={{ cursor: 'pointer' }}>
                  <small>View Errors ({lastResult.errors.length})</small>
                </summary>
                <div className="mt-1">
                  {lastResult.errors.slice(0, 5).map((error, index) => (
                    <div key={index} className="text-danger">
                      <small>• {error}</small>
                    </div>
                  ))}
                  {lastResult.errors.length > 5 && (
                    <div className="text-muted">
                      <small>... and {lastResult.errors.length - 5} more errors</small>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        {!tournament?.contractAddress && (
          <div className="mt-2">
            <small className="text-muted">
              Bulk registration is only available for tournaments with a contract address.
            </small>
          </div>
        )}
      </TournamentDetailCard>
    </div>
  );
};

export default BulkRegistrationSection;
